import { mediaApi } from "../../../media/mediaApi";
import { triggerDownload } from "../../../helpers/collectorQrDownload";
import { downloadFileExtension } from "../../../common/Components/Icons/downloadFileExtension";
import { isPreviewVideo, previewMediaUrl } from "../previewMedia";
import { normalizeView, VIEW_LABEL } from "./mediaViews";

/** One part of a file name: letters, digits and dashes only, so it survives every OS and WhatsApp. */
const part = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export type MediaNameInput = {
  /** Certificate number (the SKU, e.g. HKP4206); empty before the first save. */
  sku?: string;
  name?: string;
  loomNum?: string | number;
};

/**
 * Owner rule (2026-10-29): SKU_name_view_loomnum, e.g. HKP4206_Katan-Tanchoi_Pallu_L12.
 * The view is the photo's tag, "Video" for a video, "Photo" when untagged; a second photo of the
 * same view gets "-2". Empty parts are left out rather than leaving "__".
 */
export const mediaDownloadBaseName = (product: MediaNameInput, item: any, sameViewNumber = 1): string => {
  const tag = normalizeView(item?.view);
  const view = isPreviewVideo(item) ? "Video" : tag ? VIEW_LABEL[tag] : "Photo";
  const viewPart = sameViewNumber > 1 ? `${view}-${sameViewNumber}` : view;
  return [part(product.sku), part(product.name), viewPart, part(product.loomNum)].filter(Boolean).join("_") || "photo";
};

/** 1 for the first row with this item's view (or video / untagged), 2 for the second, and so on. */
export const sameViewNumber = (rows: any[], index: number): number => {
  const key = (row: any) => (isPreviewVideo(row) ? "VIDEO" : normalizeView(row?.view) || "");
  const target = key(rows[index]);
  return (rows || []).slice(0, index + 1).filter((row) => row && !row._deleted && key(row) === target).length;
};

/** The file could not be fetched here (a storage link without CORS); the sheet offers it as a link to tap. */
export class DownloadNeedsTap extends Error {
  constructor(readonly url: string) {
    super("Tap Open file, then save it from there.");
  }
}

const saveBlob = (blob: Blob, fileName: string) => {
  const href = URL.createObjectURL(blob);
  triggerDownload(href, fileName);
  setTimeout(() => URL.revokeObjectURL(href), 1000);
};

/**
 * Saves the studio's own file: the archived original for a media-service photo, else the stored file
 * (videos, older S3 photos) or the local copy while it is still uploading. Never a resized copy.
 */
export const downloadProductMedia = async (item: any, baseName: string): Promise<void> => {
  const assetId = String(item?.mediaAssetId || "").trim();
  let url = "";
  let type = String(item?.type || "");
  if (assetId && !isPreviewVideo(item)) {
    try {
      const original = await mediaApi.downloadOriginal(assetId);
      url = original.url;
      type = original.contentType || type;
    } catch {
      // The original is still archiving; the stored copy below is the best we have.
    }
  }
  if (!url) url = previewMediaUrl(item);
  if (!url || url.startsWith("hkp-media:")) throw new Error("This file is not ready to download yet");
  const ext = downloadFileExtension(type, url);
  const fileName = `${baseName}.${ext}`;
  try {
    const response = await fetch(url, { cache: "no-cache" });
    if (!response.ok) throw new Error("Download failed");
    saveBlob(await response.blob(), fileName);
  } catch {
    // A storage link without CORS. Opening a window here (after awaits) is blocked on iOS, so hand the
    // link back: the studio's own tap on it opens the file.
    throw new DownloadNeedsTap(url);
  }
};
